import React, {
  createRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import {
  fnCheckDuplicateSn,
  fnGetWhLocLotCtPltInv,
  fnGetWhSnInv,
  fnGetWhLocPjtLotCtPltInv,
  fnGetWhPjtSnInv,
} from "@shared/utils/InvUtils";
import * as converter from "@shared/utils/ValueConverterUtils";
import {
  applyAutoSlip,
  CalAmBulk,
  fnGetIotype,
  getInitFormData,
} from "@shared/utils/CommonUtils";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import { showDialog } from "@widgets/CommonDialog";
import { global, globalMenu } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import { showPopupModal, showResizablePopupModal } from "@widgets/CommonModal";
import SnButton from "@shared/components/SnButton";
import SnResizeBar from "@shared/components/SnResizeBar";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import { dateToString } from "@shared/utils/DateUtils";
import { RowNode } from "ag-grid-community";
import SnBarcodeBox from "@shared/components/SnBarcodeBox";
import SN_MTL_SCT_REG_004_POP_001 from "./SN_MTL_SCT_REG_004_POP_001";
import { SnTab } from "@shared/components/SnTab";
import { SnTabHeader } from "@shared/components/SnTabHeader";
import ExcelDialog from "@widgets/commonpopup/ExcelDialog";
import { ExcelUpload_S01_Validate } from "@widgets/commonpopup/excel-upload";
import SnSwitch, { SnSwitchHandle } from "@shared/components/SnSwitch";
import SnNumberBox from "@shared/components/SnNumberBox";

interface Props {
  Menuprops: menuInfo;
}

// 외주입고처리(통합)
const SN_MTL_SCT_REG_004 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo, config } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
    grid2: React.createRef<SnGridRef>(),
    grid3: React.createRef<SnGridRef>(),
    grid4: React.createRef<SnGridRef>(),
    grid5: React.createRef<SnGridRef>(),
    grid6: React.createRef<SnGridRef>(),
  });
  const txtBarcodeRef1 = useRef<any>(null);
  const txtBarcodeRef2 = useRef<any>(null);
  const sheetRef1 = useRef<any>(null);
  const isExcelUploading = useRef(false);
  const numberBoxFifoRef = useRef<any>(null);
  const switchRef = useRef<SnSwitchHandle>(null);
  const lotSnDataRef = useRef<Record<string, any>[]>([]);
  const pendingGrid6Deletes = useRef<any[]>([]);
  const iotypeRef = useRef<any[]>([]);

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid4: [],
    grid5: [],
    grid6: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid4: [],
    grid5: [],
    grid6: [],
  });

  const [selectedDocu, setSelectedDocu] = useState<string>("");
  const [selectedTab, setSelectedTab] = useState("1");
  const [focusedIndex, setFocusedIndex] = useState<any>();
  const [lotSnData, setLotSnData] = useState<Record<string, any>[]>([]);

  // MAS0002(프로젝트 사용유무). 프로젝트가 헤더가 아니라 출고 라인(grid4)에 있어
  // 재고 조회·캐시·필터를 라인의 cd_pjt 기준으로 나눈다. (MOW_006 과 동일)
  const mas0002 = config?.MAS0002?.dc_ctrl ?? "N"; // 프로젝트 사용유무 Y/N

  //#region 프로젝트 재고 헬퍼
  // 재고 캐시(lotSnData)·grid6 매칭 키. 프로젝트 사용 시 cd_pjt 까지 같아야 같은 재고로 본다.
  const sameInvKey = (a: any, b: any) =>
    a?.cd_item === b?.cd_item &&
    a?.cd_wh === b?.cd_wh &&
    (mas0002 !== "Y" || (a?.cd_pjt ?? "") === (b?.cd_pjt ?? ""));

  // grid5 필터 조건에 붙일 프로젝트 조건
  const pjtFilter = (cd_pjt: any) =>
    mas0002 === "Y" ? { cd_pjt: cd_pjt ?? "" } : {};

  // 프로젝트 재고 API 는 요청 최상위에 cd_pjt 하나만 받으므로 프로젝트별로 나눠 호출한다.
  const callByPjt = async (list: any[], api: (p: any) => Promise<any>) => {
    const groups = new Map<string, any[]>();
    list.forEach((it) => {
      const key = it.cd_pjt ?? "";
      groups.set(key, [...(groups.get(key) ?? []), { ...it, cd_pjt: key }]);
    });
    const results = await Promise.all(
      Array.from(groups.entries()).map(async ([key, group]) => {
        const rows: any[] = (await api(group)) ?? [];
        return rows.map((r) => ({ ...r, cd_pjt: r.cd_pjt ?? key }));
      })
    );
    return results.flat();
  };

  // 프로젝트 사용 시 프로젝트 재고, 아니면 기존 재고 API
  const fetchLotInv = async (params: any) => {
    const list = Array.isArray(params) ? params : [params];
    if (mas0002 !== "Y") return fnGetWhLocLotCtPltInv(list);
    return callByPjt(list, fnGetWhLocPjtLotCtPltInv);
  };
  const fetchSnInv = async (params: any) => {
    const list = Array.isArray(params) ? params : [params];
    if (mas0002 !== "Y") return fnGetWhSnInv(list);
    return callByPjt(list, fnGetWhPjtSnInv);
  };
  //#endregion

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
          grid4: rtn.grid.grid4,
          grid5: rtn.grid.grid5,
          grid6: rtn.grid.grid6,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    if (
      Menuprops.figure &&
      columns.grid1.length > 0 &&
      columns.grid2.length > 0
    ) {
      onActivate(Menuprops.nodeData);
    }
  }, [columns, Menuprops]);

  useEffect(() => {
    const fetchData = async () => {
      const data = await fnGetIotype("MTL", "1", "8", "");
      iotypeRef.current = data;
    };
    fetchData();
  }, []);

  useEffect(() => {
    lotSnDataRef.current = lotSnData;
  }, [lotSnData]);

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  const onSearch = async (searchData: any) => {
    const modifiedRowsH = getGridCurrent("grid1").getChange();
    const modifiedRowsL = getGridCurrent("grid2").getChange();
    const modifiedRowsL2 = getGridCurrent("grid4").getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0 ||
      modifiedRowsL2.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirmCancel",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) return;
      } else if (confirm === undefined) return;
    }
    try {
      const searchDataToUse = searchData || searchRef.current.getFormdata();
      const params = {
        id: "sn_mtl_sct_reg_004_01",
        params: searchDataToUse,
      };

      setLotSnData([]);
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid3").resetUpdate();
      getGridCurrent("grid4").resetUpdate();
      getGridCurrent("grid6").resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
        grid3: data[2] || [],
        grid4: data[3] || [],
        grid5: [],
        grid6: data[4] || [],
      });
    } catch (error: any) {}
  };

  const onInsert = () => {
    try {
      getGridCurrent("grid1").addRow();

      getGridCurrent("grid3").applyFilters({
        no_io: "",
        no_line: "",
      });
    } catch (error: any) {}
  };

  const onBeforeAddRow1 = (): any => {
    try {
      const modifiedRowsH = getGridCurrent("grid1").getChange();
      if (modifiedRowsH.modifiedRowcount > 0) {
        showDialog("warning", "수정된 내용이 있습니다.");
        return false;
      }
      const searchInfo = searchRef.current.getFormdata();
      const today = dateToString(new Date());
      return {
        cd_firm: userInfo?.cd_firm,
        cd_biz: searchInfo?.cd_biz,
        cd_dept: userInfo?.cd_dept,
        nm_dept: userInfo?.nm_dept,
        cd_gp: userInfo?.cd_gp,
        cd_emp: userInfo?.cd_emp,
        nm_emp: userInfo?.nm_emp,
        dt_io: today,
        fg_trns: 1,
        rt_exch: 1,
        cd_exch: "000",
        tp_slip: "8",
        fg_slip: "1",
        am: 0,
        am_k: 0,
        am_vat: 0,
        am_supply: 0,
      };
    } catch (error: any) {
      return false;
    }
  };

  const onAddRow2 = () => {
    try {
      return getGridCurrent("grid2").addRow();
    } catch (error: any) {}
  };

  const onBeforeAddRow2 = (): any => {
    try {
      getGridCurrent("grid1").stopEditing();
      const focusedNodeH = getGridCurrent("grid1").focusedRowNode();
      if (focusedNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const maxNoLine = getGridCurrent("grid2").getMaxValue("no_line");
      return {
        cd_firm: focusedNodeH.data?.cd_firm,
        cd_biz: focusedNodeH.data?.cd_biz,
        no_io: focusedNodeH.data?.no_io,
        no_line: maxNoLine + 1,
        dt_io: focusedNodeH.data?.dt_io,
        dt_rtn_plan: focusedNodeH.data?.dt_io,
        fg_slip: "1",
        tp_slip: "8",
        fg_io: focusedNodeH.data?.fg_io,
        fg_money: focusedNodeH.data?.fg_money,
        fg_map: focusedNodeH.data?.fg_map,
        yn_req: "Y",
        rt_exch: focusedNodeH.data?.rt_exch,
        fg_vat: "21",
        qt: 0,
        am: 0,
        am_k: 0,
        am_vat: 0,
        am_supply: 0,
        um: 0,
        um_k: 0,
        um_supply: 0,
        qt_good: 0,
        qt_bad: 0,
        cd_wh: focusedNodeH.data?.cd_wh,
        nm_wh: focusedNodeH.data?.nm_wh,
        cd_io: focusedNodeH.data?.cd_io,
      };
    } catch (error: any) {
      return false;
    }
  };

  const onBeforeAddRow3 = (): any => {
    try {
      const focusedRowNode = getGridCurrent("grid2").focusedRowNode();

      const updatedRow = {
        cd_firm: focusedRowNode.data?.cd_firm,
        cd_biz: focusedRowNode.data?.cd_biz,
        no_io: focusedRowNode.data?.no_io,
        no_line: focusedRowNode.data?.no_line,
        fg_slip: focusedRowNode.data?.fg_slip,
        cd_item: focusedRowNode.data?.cd_item,
        no_sn: txtBarcodeRef1.current.getValue(),
      };

      focusedRowNode.setDataValue(
        "qt_good",
        Number(focusedRowNode.data?.qt_good) + 1,
        "noEvent"
      );

      return updatedRow;
    } catch (error: any) {
      return false;
    }
  };

  //#region onCellFocused3
  const onCellFocused3 = (): any => {
    const focusedRowNode = getGridCurrent("grid3").focusedRowNode();

    if (focusedRowNode) {
      if (
        focusedRowNode.data.no_sn === "" ||
        focusedRowNode.data.no_sn === null ||
        focusedRowNode.data.no_sn === undefined
      ) {
        return;
      }
    }
    txtBarcodeRef1.current.focus();
  };
  //#endregion

  const onAddRow4 = () => {
    try {
      return getGridCurrent("grid4").addRow();
    } catch (error: any) {}
  };

  const onBeforeAddRow4 = (): any => {
    try {
      getGridCurrent("grid1")?.stopEditing();
      const focusedNodeH = getGridCurrent("grid1").focusedRowNode();
      if (focusedNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const today = dateToString(new Date());
      const maxNoLine = getGridCurrent("grid4").getMaxValue("no_line");
      return {
        cd_firm: focusedNodeH.data?.cd_firm,
        cd_biz: focusedNodeH.data?.cd_biz,
        no_io: focusedNodeH.data?.no_io,
        no_line: maxNoLine + 1,
        dt_io: focusedNodeH.data?.dt_io,
        dt_rtn_plan: focusedNodeH.data?.dt_io,
        fg_slip: "2",
        tp_slip: "8",
        fg_io: "1",
        fg_money: "1",
        fg_map: "1",
        fg_vat: "21",
        yn_req: "Y",
        rt_exch: 1,
        no_line_order: 0,
        no_line_req: 0,
        qt: 0,
        qt_good: 0,
        qt_bad: 0,
        qt_req: 0,
        cd_wh: focusedNodeH.data?.cd_cust,
      };
    } catch (error: any) {
      return false;
    }
  };

  const onDelete = async () => {
    if (getGridCurrent("grid1").getDisplayedRowCount() < 1) {
      return;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");
    if (!confirm) return;

    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
    getGridCurrent("grid3").applyFilters({
      no_io: focusedRowNodeH.data?.no_io,
    });

    const deleteKeys: { no_io: string; no_line: number }[] = [];
    const headerNoIo = focusedRowNodeH.data.no_src;
    getGridCurrent("grid4").forEachNode((node: any) => {
      if (node?.data?.no_io === headerNoIo) {
        deleteKeys.push({
          no_io: node.data.no_io,
          no_line: node.data.no_line,
        });
      }
    });

    const deleteRows: any[] = [];
    getGridCurrent("grid6").forEachNode((node: any) => {
      if (
        deleteKeys.some(
          (k) => k.no_io === node.data.no_io && k.no_line === node.data.no_line
        )
      ) {
        deleteRows.push(node.data);
      }
    });
    pendingGrid6Deletes.current = [
      ...pendingGrid6Deletes.current,
      ...deleteRows,
    ];

    // getGridCurrent("grid6").deleteAllRows();
    getGridCurrent("grid4").deleteAllRows();
    getGridCurrent("grid3").deleteAllRows();
    getGridCurrent("grid2").deleteAllRows();
    getGridCurrent("grid1").deleteSelectedRows();

    const realDeletedRows = getGridCurrent("grid1").getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid3").resetUpdate();
      getGridCurrent("grid4").resetUpdate();
      getGridCurrent("grid5").resetUpdate();
      getGridCurrent("grid6").resetUpdate();
      return;
    }

    return await onSave();
  };

  const onDeleteRow2 = async () => {
    const rowCount = getGridCurrent("grid2").getDisplayedRowCount();
    if (rowCount === 0) return;
    if (rowCount === 1) {
      await showDialog("alter", "마지막 행입니다.");
      return;
    }

    getGridCurrent("grid3").deleteAllRows();
    getGridCurrent("grid2").deleteSelectedRows();

    // 투입자재 삭제
    const focusedNodeL = getGridCurrent("grid2").focusedRowNode();
    const deleteRcvNodes: any[] = [];
    getGridCurrent("grid4").forEachFilteredNode((node: RowNode) => {
      if (node.data.cd_item_parent === focusedNodeL.data.cd_item) {
        deleteRcvNodes.push(node);
      }
    });
    for (const node of deleteRcvNodes) {
      const insertRows = getGridCurrent("grid4").getChange().insert ?? [];
      const isInsertRow = insertRows.some(
        (row: any) =>
          row.no_line === node.data.no_line && row.no_io === node.data.no_io
      );

      if (isInsertRow) {
        resetRowData();
      } else {
        // getGridCurrent("grid6").deleteAllRows();
        getGridCurrent("grid6").forEachNode((n: any) => {
          if (
            n.data.no_io === node.data.no_io &&
            n.data.no_line === node.data.no_line
          ) {
            pendingGrid6Deletes.current.push(n.data);
          }
        });
        getGridCurrent("grid6").deleteAllRows();
      }

      getGridCurrent("grid4").deleteRowByIndex(node.rowIndex);
    }
  };

  const onSave = async () => {
    try {
      getGridCurrent("grid1").stopEditing();
      getGridCurrent("grid2").stopEditing();
      getGridCurrent("grid4").stopEditing();

      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const requiredValuesCheckL =
        getGridCurrent("grid2").requiredValuesCheck();
      if (!requiredValuesCheckL) return false;

      const requiredValuesCheckL2 =
        getGridCurrent("grid4").requiredValuesCheck();
      if (!requiredValuesCheckL2) return false;

      const qtiohChanges_i = getGridCurrent("grid1").getChange();
      const qtioChanges_i = getGridCurrent("grid2").getChange();
      const qtiosnChangesOrigin_i = getGridCurrent("grid3").getChange();
      const qtioChanges_o = getGridCurrent("grid4").getChange();
      const qtiolotsnChanges_o = getGridCurrent("grid6").getChange();
      if (pendingGrid6Deletes.current.length > 0) {
        qtiolotsnChanges_o.delete = [
          ...(qtiolotsnChanges_o.delete ?? []),
          ...pendingGrid6Deletes.current,
        ];
        qtiolotsnChanges_o.modifiedRowcount +=
          pendingGrid6Deletes.current.length;
      }

      const headerInserts = qtiohChanges_i.insert ?? [];
      const lineInserts_i = qtioChanges_i.insert ?? [];
      const lineInserts_o = qtioChanges_o.insert ?? [];
      if (
        headerInserts.length > 0 &&
        (lineInserts_i.length === 0 || lineInserts_o.length === 0)
      ) {
        await showDialog(
          "alter",
          "상세 정보가 없습니다.\n행추가하여 상세정보 작성 후 저장해 주시기 바랍니다."
        );
        return false;
      }
      // return;

      if (
        qtiohChanges_i.modifiedRowcount === 0 &&
        qtioChanges_i.modifiedRowcount === 0 &&
        qtiosnChangesOrigin_i.modifiedRowcount === 0 &&
        qtioChanges_o.modifiedRowcount === 0 &&
        qtiolotsnChanges_o.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      let hasInvalid = false;
      for (const l of lineInserts_i) {
        if (
          l.fg_unit === "LOT" &&
          (l.no_lot === null || l.no_lot === undefined || l.no_lot === "")
        ) {
          hasInvalid = true;
          break;
        }
      }
      if (hasInvalid) {
        await showDialog(
          "alter",
          "LOT관리 품목의 LOT번호가 입력되지 않았습니다."
        );
        return false;
      }

      // 1. insert 배열에서 cd_item과 no_sn 추출 및 내부 중복 체크
      const inserts = qtiosnChangesOrigin_i.insert ?? [];
      const localDuplicates: string[] = []; // 내부 중복된 시리얼 번호를 담을 배열
      const seen = new Set<string>(); // 비교를 위한 임시 셋

      const snCheckList = inserts.map((item: any) => {
        const identifier = `${item.cd_item}_${item.no_sn}`; // 품목과 시리얼의 조합 키 생성

        if (item.no_sn === "") {
          hasInvalid = true;
        }
        if (seen.has(identifier)) {
          // 이미 셋에 존재한다면 화면에 입력된 데이터들끼리 중복된 것임
          if (!localDuplicates.includes(item.no_sn)) {
            localDuplicates.push(item.no_sn);
          }
        } else {
          seen.add(identifier);
        }

        return {
          cd_item: item.cd_item,
          no_sn: item.no_sn,
          cd_biz: item.cd_biz,
        };
      });

      if (hasInvalid) {
        await showDialog("alter", "시리얼 번호가 입력되지 않은 건이 있습니다.");
        return false;
      }

      // [추가된 로직] 내부 입력값 중 중복이 발견된 경우
      if (localDuplicates.length > 0) {
        await showDialog(
          "alter",
          `입력된 목록 중 중복된 시리얼 번호가 있습니다.\n${localDuplicates.join(", ")}`
        );
        return false;
      }

      // 2. 가공된 배열을 함수로 전달
      if (snCheckList.length > 0) {
        const duplicateSns = await fnCheckDuplicateSn(snCheckList);

        if (duplicateSns && duplicateSns.length > 0) {
          await showDialog(
            "alter",
            `이미 등록된 시리얼 번호가 포함되어 있습니다.\n${duplicateSns.join(", ")}`
          );
          return false;
        }
      }

      const validReq = await validateQtReq(qtioChanges_o, qtiolotsnChanges_o);
      if (!validReq) return false;

      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
      const noSlip = await applyAutoSlip({
        cd_column: "no_io",
        cd_module: "MTL",
        no_line: 4,
        dt: focusedRowNodeH.data.dt_io,
        girdrefs: [
          gridRefs.current["grid1"],
          gridRefs.current["grid2"],
          gridRefs.current["grid3"],
        ],
        inserts: [qtiohChanges_i, qtioChanges_i, qtiosnChangesOrigin_i],
        cd_menu: Menuprops.cd_menu,
      });

      // 출고 데이터의 no_src, no_line_src에 입고 no_io, no_line 넣어주기
      gridRefs.current["grid4"].current?.forEachNode((node: any) => {
        if (node.data.no_io == "" || node.data.no_io == null) {
          node.setDataValue("no_src", noSlip);
        }
      });
      qtioChanges_o.insert.forEach((row: any) => {
        if (row.no_io == "" || row.no_io == null) {
          row.no_src = noSlip;
        }
      });

      const transformForSn = (snInserts: any[]) => {
        if (!snInserts || snInserts.length === 0) return [];

        // 1. 특정 변경(insert)분이 아니라 그리드 전체에서 현재 상태의 행들을 가져옵니다.
        const allQtioRows: any[] = [];
        getGridCurrent("grid2").forEachNode((node: any) => {
          allQtioRows.push(node.data);
        });

        const result: any[] = [];

        // 2. qtiosn(시리얼 변경분)을 기준으로 루프
        snInserts.forEach((snRow: any) => {
          // 3. 전체 행 중에서 no_io와 no_line이 일치하는 부모 행을 찾습니다.
          const parentLine = allQtioRows.find((line: any) => {
            const isSameIo = (line.no_io || "") === (snRow.no_io || "");
            const isSameLine = line.no_line === snRow.no_line;
            return isSameIo && isSameLine;
          });

          if (parentLine) {
            result.push({
              ...parentLine, // 부모(qtio)의 품목 정보 등 전체 복사
              ...snRow, // 시리얼 번호(no_sn) 등 정보 덮어쓰기
              cd_cust: focusedRowNodeH.data?.cd_cust,
              no_origin: snRow.no_io, // 원 입고전표
              no_line_origin: snRow.no_line, // 원 입고항번
            });
          }
        });

        return result;
      };

      const qtiosnChanges = {
        insert: transformForSn(qtiosnChangesOrigin_i.insert ?? []),
        delete: qtiosnChangesOrigin_i.delete ?? [], // 삭제는 그대로 전달
        modifiedRowcount: 0,
      };

      qtiosnChanges.modifiedRowcount =
        qtiosnChanges.insert.length + qtiosnChanges.delete.length;

      const saveData = {
        qtioH_i: qtiohChanges_i,
        qtio_i: qtioChanges_i,
        qtio_sn_i: qtiosnChanges,
        qtio_o: qtioChanges_o,
        qtio_sn_o: qtiolotsnChanges_o,
      };

      const ok = await fnSave("SN_MTL_SCT_REG_004_SAVE_01", saveData);
      if (!ok) {
        getGridCurrent("grid1").resetUpdate(true);
        getGridCurrent("grid2").resetUpdate(true);
        getGridCurrent("grid3").resetUpdate(true);
        getGridCurrent("grid4").resetUpdate(true);
        getGridCurrent("grid6").resetUpdate(true);
        return false;
      }

      setFocusedIndex(focusedRowNodeH.rowIndex);
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid3").resetUpdate();
      getGridCurrent("grid4").resetUpdate();
      getGridCurrent("grid6").resetUpdate();
      pendingGrid6Deletes.current = [];
      setLotSnData([]);
      await onSearch(undefined);
      return true;
    } catch (error: any) {
      getGridCurrent("grid1").resetUpdate(true);
      getGridCurrent("grid2").resetUpdate(true);
      getGridCurrent("grid3").resetUpdate(true);
      getGridCurrent("grid4").resetUpdate(true);
      getGridCurrent("grid6").resetUpdate(true);
      return false;
    }
  };

  const onGrid1BtnsClick = async (id: string) => {
    const qtiohChanges = getGridCurrent("grid1").getChange().insert;
    if (qtiohChanges.length === 0) {
      await showDialog("alter", "신규행을 추가하시기 바랍니다.");
      return;
    }

    switch (id) {
      case "btn_POP1":
        const selectedFormData = searchRef.current.getFormdata();
        const selectedData = {
          cd_biz: selectedFormData.cd_biz,
          dt_from: selectedFormData.dt_from,
          dt_to: selectedFormData.dt_to,
        };

        // 신규행 포커스 잡아주기
        let focusedRowHIdx = 0;
        getGridCurrent("grid1").forEachFilteredNode((node: RowNode) => {
          if (node.data.no_io === "" || node.data.no_io === undefined) {
            focusedRowHIdx = node.rowIndex ?? focusedRowHIdx;
          }
        });

        getGridCurrent("grid1").selectAndFocusRow(focusedRowHIdx);

        const component = (
          <SN_MTL_SCT_REG_004_POP_001 selectedData={selectedData} />
        );
        const result = await showResizablePopupModal({
          title: "의뢰적용 도움",
          children: component,
          validation: true,
        });
        if (!result || result === "close") return;

        const headerResult = result[0];
        const lineResult = result[1];
        const lineResult2 = result[2];

        const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
        if (focusedRowNodeH && headerResult[0]) {
          for (const key in headerResult[0]) {
            switch (key) {
              case "cd_io":
                focusedRowNodeH.setDataValue(key, headerResult[0][key]);
                break;
              default:
                focusedRowNodeH.setDataValue(
                  key,
                  headerResult[0][key],
                  "noEvent"
                );
                break;
            }
          }
        }
        const cdItemNoLineMap = new Map<string, any>();
        lineResult.map((result: any) => {
          if (headerResult[0]) {
            const addedNode = onAddRow2();
            if (addedNode) {
              cdItemNoLineMap.set(result.cd_item, addedNode.data.no_line);
              for (const key in result) {
                if (key !== "no_line")
                  addedNode.setDataValue(key, result[key], "noEvent");
              }
              addedNode.setDataValue("cd_io", headerResult[0].cd_io, "noEvent");
              addedNode.setDataValue(
                "fg_slip",
                headerResult[0].fg_slip,
                "noEvent"
              );
              addedNode.setDataValue(
                "fg_money",
                headerResult[0].fg_money,
                "noEvent"
              );
              addedNode.setDataValue("no_line_req", result.no_line, "noEvent");
              addedNode.setDataValue("no_order", result.no_po, "noEvent");
              addedNode.setDataValue(
                "no_line_order",
                result.no_line_pol,
                "noEvent"
              );
              addedNode.setDataValue(
                "dt_io",
                focusedRowNodeH.data.dt_io,
                "noEvent"
              );
              if (result.fg_unit !== "SN") {
                addedNode.setDataValue("qt_good", result.qt_app, "noEvent");
                addedNode.setDataValue("qt", result.qt_app, "noEvent");
              } else {
                addedNode.setDataValue("qt_good", 0, "noEvent");
                addedNode.setDataValue("qt", 0, "noEvent");
              }
              getGridCurrent("grid2").applyBlock(addedNode);
            }
          }
        });
        const lotItemList: any = [];
        const snItemList: any = [];
        lineResult2.map((result: any) => {
          if (headerResult[0]) {
            const addedNode = onAddRow4();
            if (addedNode) {
              for (const key in result) {
                if (key !== "no_line") addedNode.setDataValue(key, result[key]);
              }
              addedNode.setDataValue(
                "no_line_src",
                cdItemNoLineMap.get(result.cd_item)
              );
              addedNode.setDataValue("cd_item_parent", result.cd_item);
              addedNode.setDataValue("nm_item_parent", result.nm_item);
              addedNode.setDataValue("cd_item", result.cd_matl);
              addedNode.setDataValue("nm_item", result.nm_matl);
              addedNode.setDataValue("dc_item_spec", result.dc_item_spec_matl);
              addedNode.setDataValue("dc_item_unit", result.dc_item_unit_matl);
              addedNode.setDataValue("cd_io", result.cd_io_isu);
              addedNode.setDataValue("fg_slip", result.fg_slip);
              addedNode.setDataValue("fg_money", result.fg_money);
              addedNode.setDataValue("cd_wh", focusedRowNodeH.data.cd_cust);
              addedNode.setDataValue("qt_need", result.qt_need);
              if (result.fg_unit !== "QT") {
                addedNode.setDataValue("qt", 0);
              } else {
                addedNode.setDataValue(
                  "qt_good",
                  result.qt_need * result.qt_app
                );
              }
              addedNode.setDataValue("qt_req", result.qt_need * result.qt_app);
              addedNode.setDataValue("no_line_req", result.no_line);
              addedNode.setDataValue("no_order", result.no_po);
              addedNode.setDataValue("no_line_order", result.no_line_pol);
              addedNode.setDataValue("dt_io", focusedRowNodeH.data.dt_io);

              // 프로젝트 사용(MAS0002=Y) 시 라인에 프로젝트가 없으면 재고를 불러오지 않는다
              const hasPjt =
                mas0002 !== "Y" || converter.getString(result.cd_pjt) !== "";
              if (hasPjt) {
                switch (result.fg_unit) {
                  case "LOT":
                    lotItemList.push({
                      cd_biz: focusedRowNodeH.data.cd_biz,
                      cd_item: result.cd_matl,
                      cd_wh: focusedRowNodeH.data.cd_cust,
                      cd_loc: "",
                      cd_pjt: result.cd_pjt ?? "", // 의뢰 라인의 프로젝트
                    });
                    break;
                  case "SN":
                    snItemList.push({
                      cd_biz: focusedRowNodeH.data.cd_biz,
                      cd_item: result.cd_matl,
                      cd_wh: focusedRowNodeH.data.cd_cust,
                      cd_pjt: result.cd_pjt ?? "", // 의뢰 라인의 프로젝트
                    });
                    break;
                }
              }
              getGridCurrent("grid4").applyBlock(addedNode);
            }
          }
        });
        CalAmBulk(
          gridRefs.current["grid1"],
          gridRefs.current["grid2"],
          "rt_exch"
        );
        getGridCurrent("grid1").applyBlock(focusedRowNodeH);

        if (lotItemList.length > 0) {
          const searchLotData = await fetchLotInv(lotItemList);
          const lotData = searchLotData.map((item: any) => {
            const { no_lot, ...rest } = item; // no_lot만 추출
            return {
              ...rest,
              no_barcode: no_lot, // 필드명 바꾸기
              fg_unit: "LOT", // LOT/SN구분
              qt_isu: 0, // 적용량 추가
            };
          });

          setLotSnData((prevData: any[]) => {
            // 기존에 [품목+창고]가 있는지 확인하여 없는 것만 필터링
            const newData = lotData.filter(
              (newItem: any) =>
                !prevData.some((oldItem: any) => sameInvKey(oldItem, newItem))
            );

            // 추가할 데이터가 있으면 합치고, 없으면 기존 상태 유지
            return newData.length === 0 ? prevData : [...prevData, ...newData];
          });
        }
        if (snItemList.length > 0) {
          const searchSnData = await fetchSnInv(snItemList);
          const snData = searchSnData.map((item: any) => {
            const { no_sn, ...rest } = item;
            return {
              ...rest,
              no_barcode: no_sn, // 필드명 바꾸기
              fg_unit: "SN", // LOT/SN구분
              no_src: item.no_io, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
              no_line_src: item.no_line, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
              qt_isu: 1, // 적용량 추가
              no_origin: item.no_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
              no_line_origin: item.no_line_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
            };
          });

          setLotSnData((prevData: any[]) => {
            // 기존에 [품목+창고]가 있는지 확인하여 없는 것만 필터링
            const newData = snData.filter(
              (newItem: any) =>
                !prevData.some((oldItem: any) => sameInvKey(oldItem, newItem))
            );

            // 추가할 데이터가 있으면 합치고, 없으면 기존 상태 유지
            return newData.length === 0 ? prevData : [...prevData, ...newData];
          });
        }
        break;
      case "btn_POP2":
        break;
    }
  };

  const onGrid3BtnsClick = async (id: string) => {
    switch (id) {
      case "addRow":
        let hasInvalid = false;

        getGridCurrent("grid3").forEachFilteredNode((node: RowNode) => {
          if (node.data.no_sn === "") {
            hasInvalid = true;
          }
        });
        if (hasInvalid) {
          return;
        } else {
          return getGridCurrent("grid3").addRow();
        }
      case "deleteRow":
        if (getGridCurrent("grid3").getDisplayedRowCount() < 1) return;

        const focusedRowNode = getGridCurrent("grid2").focusedRowNode();

        const insertRows = getGridCurrent("grid2").getChange().insert ?? [];
        const isInsertRow = insertRows.some(
          (row: any) =>
            row.no_line === focusedRowNode.data.no_line &&
            row.no_io === focusedRowNode.data.no_io
        );
        if (!isInsertRow) {
          await showDialog("alter", "일괄삭제만 가능합니다.");
          return;
        }

        getGridCurrent("grid3").deleteSelectedRows();

        focusedRowNode.setDataValue(
          "qt_good",
          Number(focusedRowNode.data?.qt_good) - 1
        );
        break;
    }
  };

  const onGrid6BtnsClick = async (id: string) => {
    const focusedRowNode = getGridCurrent("grid4").focusedRowNode();
    if (focusedRowNode == null) {
      await showDialog("alter", "선택된 행이 없습니다.");
      return false;
    }

    const insertRows = getGridCurrent("grid4").getChange().insert ?? [];
    const isInsertRow = insertRows.some(
      (row: any) =>
        row.no_line === focusedRowNode.data.no_line &&
        row.no_io === focusedRowNode.data.no_io
    );
    if (!isInsertRow) {
      await showDialog("alter", "신규 행에서만 입력 가능합니다.");
      return false;
    }

    if (id == "addRow") {
      await selectInv("C", "");
    }
    if (id == "deleteRow") {
      if (getGridCurrent("grid6").getDisplayedRowCount() < 1) return;

      const focusedNodeInfo = getGridCurrent("grid6").focusedRowNode();

      getGridCurrent("grid5").forEachNode((node: RowNode) => {
        if (
          sameInvKey(node.data, focusedNodeInfo.data) &&
          node.data.no_barcode === focusedNodeInfo.data?.no_barcode &&
          node.data.dt_io === focusedNodeInfo.data?.dt_io_origin
        ) {
          node.setDataValue(
            "qt_inv",
            node.data.qt_inv + focusedNodeInfo.data?.qt_good
          );

          if (node.data.fg_unit === "SN") {
            node.setDataValue("qt_isu", 1);
          }

          focusedRowNode.setDataValue(
            "qt_good",
            focusedRowNode.data?.qt_good - focusedNodeInfo.data?.qt_good
          );
          return;
        }
      });
      getGridCurrent("grid6").deleteSelectedRows();
      getGridCurrent("grid5").applyFilters({
        cd_item: focusedRowNode.data.cd_item,
        cd_wh: focusedRowNode.data.cd_wh,
        ...pjtFilter(focusedRowNode.data.cd_pjt),
        qt_inv: "notZero",
      });
    }
  };

  //#region onCellFocused6
  const onCellFocused6 = (): any => {
    txtBarcodeRef2.current.focus();
  };
  //#endregion

  const onTabChanged = (index: string) => {
    setSelectedTab(index);
  };

  const onFocusedRowChanged1 = (event: any) => {
    const selectedRow = event;
    if (selectedRow) {
      const no_io = selectedRow.no_io;
      getGridCurrent("grid2").selectAndFocusRow(0);
      getGridCurrent("grid4").selectAndFocusRow(0);
      setSelectedDocu(no_io);

      const focusedNodeL = getGridCurrent("grid2").focusedRowNode();

      if (focusedNodeL) {
        // 포커스 그대로면 지우기
        if (selectedRow.no_io !== focusedNodeL.data.no_io) {
          getGridCurrent("grid3").applyFilters({
            no_io: "",
            no_line: "",
          });
        } else {
          getGridCurrent("grid3").applyFilters({
            no_io: focusedNodeL.data.no_io,
            no_line: focusedNodeL.data.no_line,
          });
        }
      }

      getGridCurrent("grid4").applyFilters({
        no_src: selectedRow.no_io,
      });

      const rowCount = getGridCurrent("grid4").getDisplayedRowCount();

      if (rowCount > 0) {
        getGridCurrent("grid4").clearFocus();
        getGridCurrent("grid4").selectAndFocusRow(0);
      } else {
        getGridCurrent("grid6").applyFilters({
          no_src: "",
          no_line: "",
        });
      }
    } else {
      return;
    }
  };

  const onFocusedRowChanged2 = (event: any) => {
    const selectedRow = event;
    if (selectedRow) {
      getGridCurrent("grid3").applyFilters({
        no_io: selectedRow.no_io,
        no_line: selectedRow.no_line,
      });
    }
  };

  const onFocusedRowChanged4 = (event: any) => {
    const selectedRow = event;
    if (selectedRow) {
      getGridCurrent("grid5").setCheckedAllWithFilter(false);

      const insertRows = getGridCurrent("grid4").getChange().insert ?? [];
      const isInsertRow = insertRows.some(
        (row: any) =>
          row.no_line === selectedRow.no_line && row.no_io === selectedRow.no_io
      );
      if (isInsertRow) {
        getGridCurrent("grid5").applyFilters({
          cd_item: selectedRow.cd_item,
          cd_wh: selectedRow.cd_wh,
          ...pjtFilter(selectedRow.cd_pjt),
          qt_inv: "notZero",
        });
      } else {
        getGridCurrent("grid5").applyFilters({
          cd_item: "",
          cd_wh: "",
          ...pjtFilter(""),
        });
      }
    } else {
      getGridCurrent("grid5").applyFilters({
        cd_item: "",
        cd_wh: "",
        ...pjtFilter(""),
      });
    }
  };

  const onCellValueChanged1 = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;
    const rowCount = getGridCurrent("grid2").getDisplayedRowCount();
    const rowCount2 = getGridCurrent("grid4").getDisplayedRowCount();

    switch (colId) {
      case "cd_io":
        const ioType = iotypeRef.current.find(
          (item) => item.cd_io === rowData.cd_io
        );

        rowNode.setDataValue("fg_io", ioType.fg_io);
        rowNode.setDataValue("fg_money", ioType.fg_money);
        rowNode.setDataValue("fg_map", ioType.fg_map);

        if (rowCount > 0) {
          gridRefs.current["grid2"].current?.forEachFilteredNode(
            (node: RowNode) => {
              node.setDataValue("cd_io", rowData.cd_io);
              node.setDataValue("fg_io", ioType.fg_io);
              node.setDataValue("fg_money", ioType.fg_money);
              node.setDataValue("fg_map", ioType.fg_map);
            }
          );
        }
        if (rowCount2 > 0) {
          getGridCurrent("grid4").forEachFilteredNode((node: RowNode) => {
            node.setDataValue("cd_io", rowData.cd_io);
            node.setDataValue("fg_io", ioType.fg_io);
            node.setDataValue("fg_money", ioType.fg_money);
            node.setDataValue("fg_map", ioType.fg_map);
          });
        }
        break;
      case "cd_cust":
        rowNode.setDataValue("cd_wh_ref", rowData.cd_cust);
        rowNode.setDataValue("cd_cust_dlv", rowData.cd_cust);
        break;
      case "cd_wh":
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          node.setDataValue("cd_wh", rowData.cd_wh);
          node.setDataValue("nm_wh", rowData.nm_wh);
        });
        break;
      case "dt_io":
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          node.setDataValue("dt_io", rowData.dt_io);
        });
        getGridCurrent("grid3").forEachNode((node: RowNode) => {
          if (node.data.no_io === rowData.no_io) {
            node.setDataValue("dt_io", rowData.dt_io);
          }
        });
        getGridCurrent("grid4").forEachFilteredNode((node: RowNode) => {
          node.setDataValue("dt_io", rowData.dt_io);
        });
        getGridCurrent("grid6").forEachNode((node: RowNode) => {
          if (node.data.no_io === rowData.no_io) {
            node.setDataValue("dt_io", rowData.dt_io);
          }
        });
        break;
    }
  };

  const onCellValueChanged2 = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    if (colId === "cd_wh") {
      const rowCount = getGridCurrent("grid2").getDisplayedRowCount();
      if (rowCount === 1) return;
      if (!rowData.cd_wh) return;

      let apply = false;
      getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
        if (
          node.data[colId] === "" ||
          node.data[colId] === null ||
          node.data[colId] === undefined
        )
          apply = true;
      });
      if (!apply) return;

      const confirm = await showDialog(
        "confirm",
        "일괄적용하시겠습니까?(비어있는 칸이 채워집니다.)"
      );

      if (confirm) {
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          if (
            node.data.cd_wh === "" ||
            node.data.cd_wh === null ||
            node.data.cd_wh === undefined
          ) {
            node.setDataValue("cd_wh", rowData.cd_wh);
            node.setDataValue("nm_wh", rowData.nm_wh);
          }
        });
      }
    }
    switch (colId) {
      case "cd_item":
      case "cd_wh":
        if (
          rowData.cd_item !== "" ||
          rowData.cd_item !== null ||
          rowData.cd_item !== undefined
        ) {
          rowData.qt = 0;
          rowData.qt_good = 0;
          rowData.qt_bad = 0;
          rowData.no_lot = "";
          getGridCurrent("grid3").deleteAllRows();
        }
        break;
    }
    if (colId === "dt_io") {
      const rowCount = getGridCurrent("grid2").getDisplayedRowCount();
      if (rowCount === 1) return;
      if (!rowData.colId) return;
    }
    if (colId === "qt_good" || colId === "qt_bad" || colId === "um") {
      rowNode.setDataValue("qt", rowData.qt_good + rowData.qt_bad);
      CalAmBulk(
        gridRefs.current["grid1"],
        gridRefs.current["grid2"],
        "rt_exch"
      );
    }
    if (colId === "qt") {
      getGridCurrent("grid4").forEachFilteredNode((node: RowNode) => {
        if (node.data.cd_item_parent === rowData.cd_item) {
          node.setDataValue("qt_req", rowData.qt * node.data.qt_need);
        }
      });
    }
  };

  const onCellValueChanged4 = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    if (colId === "qt_good" || colId === "qt_bad") {
      rowNode.setDataValue("qt", rowData.qt_good + rowData.qt_bad);
    }

    switch (colId) {
      case "cd_wh":
      case "nm_wh":
      case "cd_pjt": // 프로젝트가 라인에 있어 프로젝트 변경도 재고 재조회
        if (colId === "cd_pjt" && mas0002 !== "Y") break;
        if (
          rowData.cd_item !== "" &&
          rowData.cd_item !== null &&
          rowData.cd_item !== undefined &&
          rowData.cd_wh !== "" &&
          rowData.cd_wh !== null &&
          rowData.cd_wh !== undefined &&
          rowData.no_req === undefined &&
          // 프로젝트 사용(MAS0002=Y) 시에는 라인의 프로젝트까지 있어야 재고를 불러온다
          (mas0002 !== "Y" || converter.getString(rowData.cd_pjt) !== "")
        ) {
          // 적용건은 여기서 돌지 않음
          // 품목이나 창고가 바뀌면 꼬일 수 있으므로 초기화
          rowData.qt = 0;
          rowData.qt_good = 0;
          rowData.qt_bad = 0;
          resetRowData();

          const data = {
            cd_biz: rowData.cd_biz,
            cd_item: rowData.cd_item,
            cd_wh: rowData.cd_wh,
            cd_loc: "",
            cd_pjt: rowData.cd_pjt ?? "",
          };

          if (!lotSnDataRef.current.some((item) => sameInvKey(item, data))) {
            switch (rowData.fg_unit) {
              case "LOT":
                const searchLotData = await fetchLotInv(data);
                const lotData = searchLotData.map((item: any) => {
                  const { no_lot, ...rest } = item; // no_lot만 추출

                  return {
                    ...rest,
                    no_barcode: no_lot, // 필드명 바꾸기
                    fg_unit: "LOT", // LOT/SN구분
                    qt_isu: 0, // 적용량 추가
                  };
                });

                // 이전 데이터에 새로 조회한 데이터 합치기
                setLotSnData((prevData) => {
                  // 이미 해당 품목+창고 데이터가 prevData에 있는지 다시 한번 확인 (상태 업데이트 시점의 최신값 기준)
                  const isAlreadyExist = prevData.some((item) =>
                    sameInvKey(item, data)
                  );

                  if (isAlreadyExist) return prevData; // 있으면 추가 안 함
                  return [...prevData, ...lotData];
                });
                break;
              case "SN":
                const searchSnData = await fetchSnInv(data);
                const snData = searchSnData.map((item: any) => {
                  const { no_sn, ...rest } = item;

                  return {
                    ...rest,
                    no_barcode: no_sn, // 필드명 바꾸기
                    fg_unit: "SN", // LOT/SN구분
                    no_src: item.no_io, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
                    no_line_src: item.no_line, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
                    qt_isu: 1, // 적용량 추가
                    no_origin: item.no_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
                    no_line_origin: item.no_line_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
                  };
                });

                // 이전 데이터에 새로 조회한 데이터 합치기
                setLotSnData((prevData) => {
                  // 이미 해당 품목+창고 데이터가 prevData에 있는지 다시 한번 확인 (상태 업데이트 시점의 최신값 기준)
                  const isAlreadyExist = prevData.some((item) =>
                    sameInvKey(item, data)
                  );

                  if (isAlreadyExist) return prevData; // 있으면 추가 안 함
                  return [...prevData, ...snData];
                });
                break;
            }
          }
          getGridCurrent("grid5").applyFilters({
            cd_item: rowData.cd_item,
            cd_wh: rowData.cd_wh,
            ...pjtFilter(rowData.cd_pjt),
            qt_inv: "notZero",
          });
        } else {
          resetRowData();
        }
        break;
    }
  };

  const resetRowData = (): any => {
    if (getGridCurrent("grid6").getDisplayedRowCount() < 1) {
      return;
    }

    getGridCurrent("grid6").forEachFilteredNode((node4: RowNode) => {
      getGridCurrent("grid5").forEachNode((node: RowNode) => {
        if (
          sameInvKey(node4.data, node.data) &&
          node4.data.no_barcode === node.data?.no_barcode
        ) {
          node.setDataValue("qt_inv", node.data.qt_inv + node4.data.qt_good);

          if (node.data?.fg_unit === "SN") {
            node.setDataValue("qt_isu", 1);
          }
          return;
        }
      });
    });
    getGridCurrent("grid4").deleteAllRows();
  };

  const onCellValueChangedLotSn = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    if (colId === "qt_isu") {
      if (rowData.qt_inv < rowData.qt_isu) {
        await showDialog("alter", "재고량을 초과했습니다.");
        rowNode.setDataValue("qt_isu", 0);
      } else {
        if (rowData.qt_isu === 0) {
          getGridCurrent("grid5").setCheckedFocusedRow(false, "noEvent");
        } else {
          getGridCurrent("grid5").setCheckedFocusedRow(true, "noEvent");
        }
      }
    }
  };

  const onBarcodeEnter1 = async (e: any) => {
    if (getGridCurrent("grid2").getDisplayedRowCount() < 1) return;

    const barcodeText = e;
    if (barcodeText === "" || barcodeText === null || barcodeText === undefined)
      return;

    const focusedRowNode = getGridCurrent("grid2").focusedRowNode();

    const insertRows = getGridCurrent("grid2").getChange().insert ?? [];
    const isInsertRow = insertRows.some(
      (row: any) =>
        row.no_line === focusedRowNode.data.no_line &&
        row.no_io === focusedRowNode.data.no_io
    );

    if (!isInsertRow) {
      await showDialog("alter", "신규 행에서만 입력 가능합니다.");
      return;
    }

    if (
      focusedRowNode.data.fg_unit === "SN" &&
      focusedRowNode.data.cd_item !== "" &&
      focusedRowNode.data.cd_item !== null &&
      focusedRowNode.data.cd_item !== undefined
    ) {
      let hasInvalid = false;
      getGridCurrent("grid3").forEachNode((node: any) => {
        if (barcodeText.toLowerCase() === node.data.no_sn.toLowerCase()) {
          hasInvalid = true;
        }
      });
      if (hasInvalid) {
        await showDialog("alter", "이미 입력한 시리얼 번호입니다.");
        txtBarcodeRef1.current.focus();
        return;
      }

      const params = {
        cd_biz: focusedRowNode.data.cd_biz,
        cd_item: focusedRowNode.data.cd_item,
        no_sn: barcodeText,
      };

      const data = await fnCheckDuplicateSn(params);
      if (data && data.length > 0) {
        await showDialog(
          "alter",
          `이미 등록된 시리얼 번호입니다: ${barcodeText}`
        );
        txtBarcodeRef1.current.focus();
        return;
      }

      getGridCurrent("grid3").addRow();
    }
  };

  const onBarcodeEnter2 = async (e: any) => {
    if (getGridCurrent("grid4").getDisplayedRowCount() < 1) return;

    const focusedRowNodeL = getGridCurrent("grid4").focusedRowNode();

    getGridCurrent("grid5").applyFilters({
      cd_item: focusedRowNodeL.data.cd_item,
      cd_wh: focusedRowNodeL.data.cd_wh,
      ...pjtFilter(focusedRowNodeL.data.cd_pjt),
      qt_inv: "notZero",
    });

    const barcode = e;
    if (barcode === "" || barcode === null || barcode === undefined) return;

    // 스캔로직
    if (!switchRef.current?.getChecked()) {
      await selectInv("S", barcode);
    } else {
      getGridCurrent("grid5").applyLikeFilters(
        {
          cd_item: focusedRowNodeL.data.cd_item,
          cd_wh: focusedRowNodeL.data.cd_wh,
          ...pjtFilter(focusedRowNodeL.data.cd_pjt),
          qt_inv: "notZero",
          no_barcode: barcode,
          no_ct: barcode,
          no_plt: barcode,
        },
        ["no_barcode", "no_ct", "no_plt"]
      );
    }
  };

  //#region selectInv
  const selectInv = async (id: string, barcode: string = "") => {
    const focusedRowNodeL = getGridCurrent("grid4").focusedRowNode();

    if (focusedRowNodeL == null) {
      await showDialog("alter", "선택된 행이 없습니다.");
      return false;
    }

    if (focusedRowNodeL.data.state !== "insert") {
      await showDialog("alter", "신규 행에서만 수정 가능합니다.");
      return false;
    }

    // 투입대상 건(qt_req > 0)이면 출고정보 합계가 투입대상량을 넘지 않도록 검증
    const qtReq = focusedRowNodeL.data.qt_req;
    const qtFooter = getGridCurrent("grid6").getFooterData().qt_good;
    const isOverReq = (qt: number) => qtReq !== 0 && qtReq < qt + qtFooter;

    let selectedMap = new Map<string, number>(); // 재고선택 키/수량 Map
    let qtAdd = 0,
      qtRemain = 0;

    switch (id) {
      case "F": // 선입선출
        if (numberBoxFifoRef.current.getValue == 0) {
          await showDialog("alter", "수량이 입력되지 않았습니다.");
          return;
        }
        qtRemain = Number(numberBoxFifoRef.current.getValue);

        if (isOverReq(qtRemain)) {
          await showDialog("alter", "출고수량이 투입대상량보다 많습니다.");
          return;
        }
        break;
      case "C": // 재고선택
        getGridCurrent("grid5").stopEditing();
        const selectedLotSn = getGridCurrent("grid5").getCheckedRows();
        if (selectedLotSn.length < 1) {
          await showDialog("alter", "선택된 재고가 없습니다.");
          return false;
        }

        let qtChecked = 0;
        selectedLotSn.forEach((row) => {
          const key = `${row.no_barcode}_${row.no_ct}_${row.no_plt}_${row.dt_io}`;
          // 사용자가 그리드5에 입력한 '출고예정수량(qt_isu)'을 가져옴
          const qtIsu = Number(row.qt_isu) || 0;
          selectedMap.set(key, qtIsu);
          qtChecked += qtIsu;
        });

        if (isOverReq(qtChecked)) {
          await showDialog("alter", "출고수량이 투입대상량보다 많습니다.");
          return;
        }
        break;
      case "S": // 스캔
        // 투입대상량 있으면 투입대상량이 잔량
        if (focusedRowNodeL.data.qt_req !== 0) {
          qtRemain = focusedRowNodeL.data.qt_req - focusedRowNodeL.data.qt;

          if (qtRemain === 0) {
            await showDialog("alter", "투입대상량이 다 등록되었습니다.");
            return false;
          }
        }
        // 투입대상량이 없으면 바코드 전체 선택
        else {
          qtRemain = -1;
        }
        break;
    }

    getGridCurrent("grid5").forEachFilteredNode((node: RowNode) => {
      const row = node.data;
      let qtIsu = 0;

      switch (id) {
        case "F": // 선입선출
          if (qtRemain <= 0) return;

          qtIsu = Math.min(row.qt_inv, qtRemain); // 재고량이 적으면 재고량 아니면 출고잔량
          break;
        case "C": // 선택
          const key = `${row.no_barcode}_${row.no_ct}_${row.no_plt}_${row.dt_io}`;
          if (!selectedMap.has(key)) return;

          qtIsu = selectedMap.get(key) || 0; // 선택량 가져옴
          break;
        case "S": // 스캔
          // qtRemain === -1은 투입대상량 없는 건임 바코드찍은만큼 전체 출고
          if (
            String(row.no_barcode ?? "").toLowerCase() ===
              barcode.toLowerCase() ||
            String(row.no_ct ?? "").toLowerCase() === barcode.toLowerCase() ||
            String(row.no_plt ?? "").toLowerCase() === barcode.toLowerCase()
          ) {
            if (qtRemain !== -1) {
              if (qtRemain <= 0) return;

              qtIsu = Math.min(row.qt_inv, qtRemain); // 재고량이 적으면 재고량 아니면 출고잔량
            } else {
              qtIsu = row.qt_inv;
            }
          }
          break;
      }

      if (qtIsu <= 0) return;

      const newRow = {
        cd_firm: focusedRowNodeL.data?.cd_firm,
        cd_biz: focusedRowNodeL.data?.cd_biz,
        no_io: focusedRowNodeL.data?.no_io,
        no_line: focusedRowNodeL.data?.no_line,
        dt_io: focusedRowNodeL.data?.dt_io,
        tp_slip: focusedRowNodeL.data?.tp_slip,
        fg_slip: focusedRowNodeL.data?.fg_slip,
        fg_io: focusedRowNodeL.data?.fg_io,
        fg_money: focusedRowNodeL.data?.fg_money,
        cd_io: focusedRowNodeL.data?.cd_io,
        cd_item: focusedRowNodeL.data?.cd_item,
        cd_wh: focusedRowNodeL.data?.cd_wh,
        no_barcode: node.data.no_barcode,
        fg_unit: node.data.fg_unit,
        qt_good: qtIsu,
        no_ct: node.data.no_ct,
        no_plt: node.data.no_plt,
        no_src: node.data.no_src, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
        no_line_src: node.data.no_line_src, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
        dt_io_origin: node.data.dt_io, // 원 LOT 입고일자
        no_origin: node.data.no_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
        no_line_origin: node.data.no_line_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
        cd_pjt: focusedRowNodeL.data?.cd_pjt ?? "", // 라인 프로젝트(도착 프로젝트 없음)
      };

      // qtRemain === -1은 투입대상량 없는 건
      if (qtRemain !== -1) {
        qtRemain -= qtIsu; // 잔량 빼주고
      }
      qtAdd += qtIsu; // 총 출고량 더하고

      node.setDataValue("qt_isu", 0);
      node.setDataValue("qt_inv", node.data.qt_inv - qtIsu);

      let isExist = false;
      // 기존항목에 있으면 수량만 업데이트
      getGridCurrent("grid6").forEachFilteredNode((node4: RowNode) => {
        if (
          sameInvKey(node4.data, focusedRowNodeL.data) &&
          node4.data.no_barcode === node.data.no_barcode &&
          node4.data.dt_io_origin === node.data.dt_io &&
          node4.data.no_ct === node.data.no_ct &&
          node4.data.no_plt === node.data.no_plt
        ) {
          node4.setDataValue("qt_good", node4.data.qt_good + qtIsu);
          isExist = true;
          return;
        }
      });
      if (!isExist) {
        getGridCurrent("grid6").addRow(newRow);
      }
    });

    focusedRowNodeL.setDataValue(
      "qt_good",
      focusedRowNodeL.data?.qt_good + qtAdd
    );
    numberBoxFifoRef.current.setValue(0);

    getGridCurrent("grid5").clearCheckBox();
    getGridCurrent("grid5").refreshFooter();
    getGridCurrent("grid6").refreshFooter();
    getGridCurrent("grid5").applyFilters({
      cd_item: focusedRowNodeL.data.cd_item,
      cd_wh: focusedRowNodeL.data.cd_wh,
      ...pjtFilter(focusedRowNodeL.data.cd_pjt),
      qt_inv: "notZero",
    });

    if (id === "S") {
      if (qtAdd === 0) {
        await showDialog("alter", "바코드 정보를 찾을 수 없습니다.");
      }
    }
  };
  //#endregion

  const validateQtReq = async (qtioChanges_o: any, qtiolotsnChanges_o: any) => {
    const qtioInsert = qtioChanges_o?.insert || [];
    const lotInsert = qtiolotsnChanges_o?.insert || [];

    // LOT/SN qt_good 합계 구하기
    const lotSumMap: Record<string, number> = {};

    lotInsert.forEach((row: any) => {
      if (row.fg_unit !== "QT") {
        const key = `${row.no_io}_${row.no_line}`;
        lotSumMap[key] = (lotSumMap[key] || 0) + Number(row.qt_good || 0);
      }
    });

    // qt_req와 비교
    for (const row of qtioInsert) {
      if (row.fg_unit === "QT") continue;
      const key = `${row.no_io}_${row.no_line}`;
      const lotSum = lotSumMap[key] || 0;
      const qtReq = Number(row.qt_req || 0);

      if (lotSum !== qtReq) {
        await showDialog(
          "alter",
          `항번 ${row.no_line} : 투입대상량(${qtReq})과 출고수량(${lotSum})이 일치하지 않습니다.`
        );
        return false;
      }
    }

    return true;
  };

  const onExcel = async () => {
    const focusedRowNode = getGridCurrent("grid2").focusedRowNode();
    if (focusedRowNode.data.fg_unit !== "SN") {
      await showDialog("alter", "시리얼 등록 품목이 아닙니다.");
      return;
    }
    const figure = await showPopupModal({
      title: "Excel",
      children: <ExcelDialog />,
      width: "360px",
      height: "200px",
    });

    if (!figure || figure === "close") return;

    if (figure === "download") {
      await onExcelDownLoad();
    } else if (figure === "upload") {
      await onExcelUpload();
    }
  };

  const onExcelDownLoad = async () => {
    try {
      const displayedCols = sheetRef1.current?.getAllDisplayedColumns() || [];
      const columnKeys = displayedCols.map((c: any) => c.getColId());
      sheetRef1.current?.exportDataAsExcel({
        columnKeys,
        skipPinnedBottom: true,
        fileName: `${globalMenu.getState().activeMenuInfo.nm_menu}.xlsx`,
        sheetName: "Sheet1",
      });
    } catch (error: any) {
      await showDialog("error", error.message);
    }
  };

  const onExcelUpload = async () => {
    try {
      const focusedRowNode = getGridCurrent("grid2").focusedRowNode();
      const defaultNoReq = focusedRowNode.data.no_req;
      const provideDefaults = (row: any) => ({
        cd_biz: row.cd_biz ?? userInfo?.cd_biz,
        no_req: row.no_req ?? defaultNoReq,
      });

      const modalRef = createRef<any>();
      const childRef = createRef<any>();
      const s01 = (
        <ExcelUpload_S01_Validate
          ref={childRef}
          modalRef={modalRef}
          Menuprops={Menuprops}
          grouping={["no_req", "no_line", "cd_item", "nm_item", "no_sn"]}
          provideDefaults={provideDefaults}
        />
      );

      const result = await showPopupModal({
        title: "SN(시리얼) 엑셀 업로드",
        children: s01,
        width: "80%",
        height: "90VH",
        okayBtnText: "다음",
      });
      if (!result) return;

      const headerResult = result.validRows ?? [];

      const grid2 = getGridCurrent("grid2");
      const grid5 = getGridCurrent("grid5");
      isExcelUploading.current = true;
      for (const snRow of headerResult) {
        let parentNode: any = null;
        const targetItem = String(snRow.cd_item ?? "").trim();
        const targetNoReq = String(defaultNoReq ?? "").trim();
        grid2.forEachNode((node: any) => {
          const nodeItem = String(node.data?.cd_item ?? "").trim();
          const nodeNoReq = String(node.data?.no_req ?? "").trim();
          if (nodeItem === targetItem && nodeNoReq === targetNoReq) {
            parentNode = node;
          }
        });
        if (!parentNode) continue;

        const addedNode = grid5.addRow();
        if (!addedNode) continue;

        const parentData = parentNode.data;

        addedNode.setDataValue("cd_firm", parentData.cd_firm, "noEvent");
        addedNode.setDataValue("cd_biz", parentData.cd_biz, "noEvent");
        addedNode.setDataValue("no_req", parentData.no_req, "noEvent");
        addedNode.setDataValue("no_line", parentData.no_line, "noEvent");
        addedNode.setDataValue("cd_item", parentData.cd_item, "noEvent");

        addedNode.setDataValue("no_sn", snRow.no_sn, "noEvent");
        parentNode.setDataValue(
          "qt_good",
          Number(parentNode.data.qt_good ?? 0) + 1
        );

        parentNode.setDataValue("qt", Number(parentNode.data.qt ?? 0) + 1);
      }
      isExcelUploading.current = false;
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  const onActivate = async (nodeData: any) => {
    if (!nodeData) return;
    if ((Menuprops.figure as string) === "fg_drilldown.Search") {
      const selectedData = {
        cd_firm: nodeData.cd_firm,
        cd_biz: nodeData.cd_biz,
        no_io: nodeData.no_io,
        dt_from: nodeData.dt_io,
        dt_to: nodeData.dt_io,
      };
      const searchData = {
        ...formData,
        ...selectedData,
      };
      setFormData(searchData);
      onSearch(searchData);
    }
  };

  const onRowDataReady = () => {
    if (focusedIndex) {
      getGridCurrent("grid1").selectAndFocusRow(focusedIndex);
    }
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged: onFocusedRowChanged1,
          onCellValueChanged: onCellValueChanged1,
          onBeforeAddRow: onBeforeAddRow1,
          onRowDataReady: onRowDataReady,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onFocusedRowChanged: onFocusedRowChanged2,
          onBeforeAddRow: onBeforeAddRow2,
          onCellValueChanged: onCellValueChanged2,
        },
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
        events: {
          onBtnAddClick: onGrid3BtnsClick,
          onBtnDeleteClick: onGrid3BtnsClick,
          onBeforeAddRow: onBeforeAddRow3,
          onCellFocused: onCellFocused3,
        },
      },
      grid4: {
        columns: columns.grid4,
        dataSource: dataSources.grid4,
        events: {
          onFocusedRowChanged: onFocusedRowChanged4,
          onBeforeAddRow: onBeforeAddRow4,
          onCellValueChanged: onCellValueChanged4,
        },
      },
      grid5: {
        columns: columns.grid5,
        dataSource: dataSources.grid5,
        events: { onCellValueChanged: onCellValueChangedLotSn },
      },
      grid6: {
        columns: columns.grid6,
        dataSource: dataSources.grid6,
        events: {
          onBtnAddClick: onGrid6BtnsClick,
          onBtnDeleteClick: onGrid6BtnsClick,
          onCellFocused: onCellFocused6,
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>

      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="30.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "single", title: "입고정보" }}
            events={grids.grid1.events}
            header={
              <div>
                <SnButton
                  text={"요청적용"}
                  onClick={() => {
                    onGrid1BtnsClick("btn_POP1");
                  }}
                />
              </div>
            }
            filter={{
              refs: [gridRefs.current["grid2"]],
              columns: ["no_io"],
            }}
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="60.5%">
          <SnTab
            titles={["입고내역", "투입내역"]}
            width={"100%"}
            onTabChanged={onTabChanged}
            header={[
              <SnTabHeader onBtnDeleteClick={onDeleteRow2} />,
              <SnTabHeader />,
            ]}
            body={[
              <div className="flex w-full h-full min-w-0 space-x-1.5">
                <SnResizeBar direction={"width"} id="panel3" orgSize="70%">
                  <SnGrid
                    ref={gridRefs.current["grid2"]}
                    columnConfig={grids.grid2.columns}
                    rowData={grids.grid2.dataSource}
                    etcProps={{ rowSelection: "single" }}
                    events={grids.grid2.events}
                  />
                </SnResizeBar>
                <SnGrid
                  ref={gridRefs.current["grid3"]}
                  columnConfig={grids.grid3.columns}
                  rowData={grids.grid3.dataSource}
                  etcProps={{ rowSelection: "single", title: "SN" }}
                  events={grids.grid3.events}
                  enablePasteOnlyAdd={true}
                  header={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <div>
                        <SnBarcodeBox
                          onEnter={onBarcodeEnter1}
                          ref={txtBarcodeRef1}
                          inputRef={useRef(null)}
                        />
                      </div>
                    </div>
                  }
                />
              </div>,
              <div className="flex w-full h-full min-w-0 space-x-1.5">
                <SnResizeBar direction={"width"} id="panel4" orgSize="60%">
                  <SnGrid
                    ref={gridRefs.current["grid4"]}
                    columnConfig={grids.grid4.columns}
                    rowData={grids.grid4.dataSource}
                    etcProps={{ rowSelection: "single" }}
                    events={grids.grid4.events}
                    filter={{
                      refs: [gridRefs.current["grid6"]],
                      columns: ["no_io", "no_line"],
                    }}
                  />
                </SnResizeBar>
                <div className="flex-1 min-w-0 flex flex-col h-full space-y-1.5">
                  <SnResizeBar direction={"height"} id="panel5" orgSize="50%">
                    <SnGrid
                      ref={gridRefs.current["grid5"]}
                      columnConfig={grids.grid5.columns}
                      rowData={lotSnData}
                      etcProps={{
                        rowSelection: "single",
                        title: "재고리스트",
                        ischeckboxSelection: true,
                      }}
                      events={grids.grid5.events}
                      header={
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <SnSwitch
                            ref={switchRef}
                            labelCheck="검색"
                            labelUncheck="스캔"
                            defaultChecked={false}
                          />
                          <div>
                            <SnBarcodeBox
                              onEnter={onBarcodeEnter2}
                              inputRef={txtBarcodeRef2}
                            />
                          </div>
                          <div className="w-[80px]">
                            <SnNumberBox
                              inputRef={numberBoxFifoRef}
                              customFormat="QT"
                            />
                          </div>
                          <div>
                            <SnButton
                              text="선입선출"
                              onClick={() => {
                                selectInv("F", "");
                              }}
                            />
                          </div>
                        </div>
                      }
                    />
                  </SnResizeBar>
                  <div className="flex-1 min-h-0">
                    <SnGrid
                      ref={gridRefs.current["grid6"]}
                      columnConfig={grids.grid6.columns}
                      rowData={grids.grid6.dataSource}
                      etcProps={{ rowSelection: "single", title: "출고정보" }}
                      events={grids.grid6.events}
                    />
                  </div>
                </div>
              </div>,
            ]}
          />
        </SnResizeBar>
        <SnFileUploadBox selectedDocu={selectedDocu} selectedLineDocu={0} />
      </div>
    </div>
  );
});

export default SN_MTL_SCT_REG_004;
